<template>
  <div class="space-y-2">
    <div class="relative">
      <input
        v-model="searchTerm"
        type="text"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        :placeholder="placeholder"
        autocomplete="off"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <div
        v-if="showDropdown"
        class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
      >
        <div
          v-if="searchTerm.trim().length < 2"
          class="px-4 py-3 text-center text-sm text-gray-500"
        >
          Tapez au moins 2 lettres…
        </div>
        <div v-else-if="isLoading" class="px-4 py-3 text-center">
          <div class="flex items-center justify-center space-x-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <span class="text-sm text-gray-500">Recherche en cours...</span>
          </div>
        </div>
        <div v-else-if="buyers.length > 0">
          <div
            v-for="buyer in buyers"
            :key="buyer.id"
            class="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
            @mousedown.prevent="selectBuyer(buyer)"
          >
            <div class="font-medium text-gray-900">{{ buyer.nomSociete }}</div>
            <div class="text-sm text-gray-500">{{ buyer.email }}</div>
          </div>
        </div>
        <div v-else class="px-4 py-3 text-center text-gray-500">
          Aucun acheteur trouvé
        </div>
      </div>
    </div>

    <div class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600">
      <div v-if="selectedBuyer" class="text-gray-900">
        <div class="font-medium">{{ selectedBuyer.nomSociete }}</div>
        <div class="text-sm text-gray-500">{{ selectedBuyer.email }}</div>
      </div>
      <div v-else class="text-gray-500 italic">
        Laissez vide si pour tout acheteur
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { searchBuyers } from '@/api/buyers'
import { Buyer } from '@/types/buyer'

interface Props {
  modelValue?: number | null
  label?: string
  placeholder?: string
  required?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: number | null): void
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Acheteur',
  placeholder: 'Rechercher un acheteur...',
  required: false,
})

const emit = defineEmits<Emits>()

const searchTerm = ref('')
const buyers = ref<Buyer[]>([])
const showDropdown = ref(false)
const selectedBuyer = ref<Buyer | null>(null)
const isLoading = ref(false)
let searchTimeout: ReturnType<typeof setTimeout> | null = null
let requestSeq = 0

async function runSearch(rawTerm: string): Promise<void> {
  const term = rawTerm.trim()
  if (term.length < 2) {
    buyers.value = []
    return
  }

  const seq = ++requestSeq
  isLoading.value = true
  try {
    const results = await searchBuyers(term)
    if (seq !== requestSeq) return
    buyers.value = results
  } catch {
    if (seq !== requestSeq) return
    buyers.value = []
  } finally {
    if (seq === requestSeq) isLoading.value = false
  }
}

function handleInput(): void {
  if (searchTimeout) clearTimeout(searchTimeout)

  showDropdown.value = true
  if (searchTerm.value.trim().length < 2) {
    buyers.value = []
    return
  }

  searchTimeout = setTimeout(() => {
    void runSearch(searchTerm.value)
  }, 300)
}

function handleFocus(): void {
  showDropdown.value = true
  if (searchTerm.value.trim().length >= 2 && buyers.value.length === 0) {
    void runSearch(searchTerm.value)
  }
}

function handleBlur(): void {
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

function selectBuyer(buyer: Buyer): void {
  selectedBuyer.value = buyer
  emit('update:modelValue', buyer.id)
  showDropdown.value = false
  searchTerm.value = ''
  buyers.value = []
}

function clearSelection(): void {
  selectedBuyer.value = null
  searchTerm.value = ''
  buyers.value = []
  emit('update:modelValue', null)
}

function handleClickOutside(event: Event): void {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    showDropdown.value = false
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue === null || newValue === undefined) {
      clearSelection()
    } else if (selectedBuyer.value?.id !== newValue) {
      const buyer = buyers.value.find((b) => b.id === newValue)
      if (buyer) selectedBuyer.value = buyer
    }
  },
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (searchTimeout) clearTimeout(searchTimeout)
})

defineExpose({
  clearSelection,
})
</script>

<style scoped>
.relative {
  position: relative;
}
</style>
